import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, LifeBuoy, Loader2, Pencil, Send, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = '/api';
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 120; // 4 minutes

async function pollTicketJob(jobId) {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const { data } = await axios.get(`${API}/tickets/job/${jobId}`);
    if (data.status === 'completed') {
      return { ticketUrl: data.ticket_url };
    }
    if (data.status === 'error') {
      const err = new Error(data.error_message || 'Création de ticket échouée');
      err.screenshot = data.failure_screenshot_base64 || null;
      err.needsArcSession = Boolean(data.needs_arc_session);
      throw err;
    }
  }
  throw new Error("La création du ticket a dépassé le délai d'attente");
}

function TicketMetadataRow({ ccEmail, priorityLabel }) {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-sm border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">
      <span><span className="text-zinc-500">Cc :</span> <span className="text-zinc-200">{ccEmail}</span></span>
      <span><span className="text-zinc-500">Priorité :</span> <span className="text-zinc-200">{priorityLabel}</span></span>
      <span><span className="text-zinc-500">Destinataire :</span> <span className="text-zinc-200">support.arc-logiciels.ch</span></span>
    </div>
  );
}

function SubmissionErrorPanel({ message, screenshotBase64, needsArcSession }) {
  return (
    <div data-testid="ticket-error-panel" className="space-y-3 rounded-sm border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
      <p className="font-semibold">L'envoi a échoué.</p>
      <p className="whitespace-pre-wrap text-red-100/90">{message}</p>
      {needsArcSession ? (
        <div className="rounded-sm border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-100">
          <p className="mb-2 font-semibold">Pour établir la session Arc une fois pour toutes :</p>
          <ol className="list-inside list-decimal space-y-1 pl-2">
            <li>Ouvre la page « Crawler Web » (menu à gauche)</li>
            <li>Saisis <code className="rounded-sm bg-black/30 px-1">https://support.arc-logiciels.ch/support/tickets</code></li>
            <li>Choisis « Authentification : Google SSO », coche « Mémoriser la session »</li>
            <li>Lance un crawl court (1-2 URLs suffisent) pour que Playwright capture les cookies</li>
            <li>Reviens ici et retente l'envoi du ticket</li>
          </ol>
        </div>
      ) : null}
      {screenshotBase64 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs uppercase tracking-wider text-red-300">Capture d'écran du portail Arc au moment de l'échec</summary>
          <img
            alt="Capture d'écran du portail Arc"
            src={`data:image/png;base64,${screenshotBase64}`}
            className="mt-2 w-full rounded-sm border border-zinc-700"
          />
        </details>
      ) : null}
    </div>
  );
}

function ArcCredentialsForm({ initialEmail = '', onSaved, onCancel }) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!email.trim() || !password) {
      toast.error('Email et mot de passe Google requis');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tickets/arc-credentials`, {
        google_email: email.trim(),
        google_password: password,
      });
      toast.success('Identifiants Arc enregistrés (chiffrés)');
      onSaved({ configured: true, google_email: email.trim().toLowerCase() });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer les identifiants");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="arc-credentials-form" className="space-y-3 rounded-sm border border-zinc-800 bg-zinc-900/60 p-3">
      <p className="text-xs text-zinc-400">
        Tes identifiants Google sont chiffrés (Fernet dérivé du secret JWT de l'app) et servent uniquement à
        ouvrir une session Arc automatiquement lors de l'envoi d'un ticket. Tu peux les supprimer à tout moment.
      </p>
      <Input
        type="email"
        data-testid="arc-google-email-input"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Email Google (ex: florence@braux.ch)"
        disabled={saving}
      />
      <Input
        type="password"
        data-testid="arc-google-password-input"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder="Mot de passe Google"
        disabled={saving}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" disabled={saving} onClick={onCancel}>Annuler</Button>
        <Button type="button" size="sm" disabled={saving} onClick={handleSave} data-testid="arc-credentials-save-btn">
          {saving ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Enregistrement...</> : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
}

function ArcSessionPanel() {
  const [status, setStatus] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [jsonPayload, setJsonPayload] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/arc-session`);
      setStatus(data);
    } catch {
      setStatus({ active: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async () => {
    if (!jsonPayload.trim()) {
      toast.error('Colle le JSON des cookies');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tickets/arc-session`, { cookies_json: jsonPayload });
      toast.success('Session Arc importée');
      setJsonPayload('');
      setShowImport(false);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'importer la session");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await axios.delete(`${API}/tickets/arc-session`);
    toast.success('Session Arc supprimée');
    await refresh();
  };

  if (!status) return null;

  if (status.active && !showImport) {
    return (
      <div data-testid="arc-session-panel" className="flex items-center justify-between gap-3 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          <span>Session Arc active ({status.cookies_count} cookies) — Cc & Priorité seront appliqués</span>
        </div>
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowImport(true)}>
            <Pencil className="h-3 w-3" /> Réimporter
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={handleDelete}>
            <Trash2 className="h-3 w-3" /> Supprimer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="arc-session-panel" className="space-y-3 rounded-sm border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-100">
      <p className="font-semibold">Session Arc non établie — les champs Cc et Priorité seront ignorés sans cette session.</p>
      <ol className="list-inside list-decimal space-y-1 pl-2">
        <li>Installe l'extension <strong>Cookie-Editor</strong> sur ton navigateur (<a className="underline" href="https://cookie-editor.com/" target="_blank" rel="noopener noreferrer">cookie-editor.com</a> — Chrome/Firefox/Edge)</li>
        <li>Connecte-toi normalement à <a className="underline" href="https://support.arc-logiciels.ch" target="_blank" rel="noopener noreferrer">support.arc-logiciels.ch</a> (SSO Google, 2FA OK)</li>
        <li>Ouvre l'icône Cookie-Editor sur la page Arc → clic sur <strong>« Export »</strong> → <strong>« JSON »</strong></li>
        <li>Colle le JSON ci-dessous et clique « Enregistrer »</li>
      </ol>
      <Textarea
        data-testid="arc-cookies-json-input"
        value={jsonPayload}
        onChange={(event) => setJsonPayload(event.target.value)}
        rows={5}
        placeholder='[{"name":"_helpkit_session","value":"...","domain":"support.arc-logiciels.ch","path":"/",...}]'
        className="font-mono text-xs"
        disabled={saving}
      />
      <div className="flex gap-2">
        {status.active ? (
          <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => { setShowImport(false); setJsonPayload(''); }}>Annuler</Button>
        ) : null}
        <Button type="button" size="sm" disabled={saving} onClick={handleSave} data-testid="arc-session-save-btn">
          {saving ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Enregistrement...</> : 'Enregistrer la session'}
        </Button>
      </div>
    </div>
  );
}

function ArcCredentialsPanel() {
  const [status, setStatus] = useState(null); // { configured, google_email }
  const [editing, setEditing] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tickets/arc-credentials`);
        setStatus(data);
        setEditing(!data.configured);
      } catch {
        setStatus({ configured: false });
        setEditing(true);
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, []);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tickets/arc-credentials`);
      toast.success('Identifiants Arc supprimés');
      setStatus({ configured: false });
      setEditing(true);
    } catch {
      toast.error('Impossible de supprimer les identifiants');
    }
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-xs text-zinc-400">
        <Loader2 className="h-3 w-3 animate-spin" /> Vérification de la session Arc...
      </div>
    );
  }

  if (editing) {
    return (
      <ArcCredentialsForm
        initialEmail={status?.google_email || ''}
        onSaved={(s) => { setStatus(s); setEditing(false); }}
        onCancel={() => {
          if (status?.configured) setEditing(false);
        }}
      />
    );
  }

  return (
    <div data-testid="arc-credentials-panel" className="flex items-center justify-between gap-3 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4" />
        <span>Connexion auto Arc configurée pour <strong>{status.google_email}</strong></span>
      </div>
      <div className="flex gap-1">
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setEditing(true)}>
          <Pencil className="h-3 w-3" /> Modifier
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={handleDelete}>
          <Trash2 className="h-3 w-3" /> Supprimer
        </Button>
      </div>
    </div>
  );
}

export function CreateTicketModal({ open, onOpenChange, messageId }) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null); // { message, screenshot }

  const fetchPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreview(null);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/tickets/preview`, { message_id: messageId });
      setPreview(data);
      setTitle(data.title);
      setDescription(data.description);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de générer le ticket');
      onOpenChange(false);
    } finally {
      setLoadingPreview(false);
    }
  }, [messageId, onOpenChange]);

  useEffect(() => {
    if (open && messageId) {
      fetchPreview();
    } else {
      setPreview(null);
      setTitle('');
      setDescription('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, messageId, fetchPreview]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Titre et description requis');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data: job } = await axios.post(`${API}/tickets/send`, {
        message_id: messageId,
        title,
        description,
      });
      const { ticketUrl } = await pollTicketJob(job.job_id);
      toast.success('Ticket créé avec succès', {
        description: ticketUrl,
        action: ticketUrl ? { label: 'Ouvrir', onClick: () => window.open(ticketUrl, '_blank', 'noopener,noreferrer') } : undefined,
        duration: 10000,
      });
      onOpenChange(false);
    } catch (err) {
      setError({ message: err.message || 'Erreur inconnue', screenshot: err.screenshot || null, needsArcSession: err.needsArcSession || false });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ticket-modal" className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Créer un ticket de support Arc</DialogTitle>
          <DialogDescription>
            Une synthèse est générée par Claude Opus 4.7 puis soumise automatiquement sur
            <code className="mx-1 rounded-sm bg-zinc-800 px-1 py-0.5 text-xs">support.arc-logiciels.ch</code>
            avec la priorité « Élevé » et <code className="mx-1 rounded-sm bg-zinc-800 px-1 py-0.5 text-xs">guillaume@braux.ch</code> en copie.
          </DialogDescription>
        </DialogHeader>

        {loadingPreview ? (
          <div className="flex items-center justify-center py-12 text-sm text-zinc-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Synthèse en cours via Claude Opus 4.7...
          </div>
        ) : (
          <div className="space-y-4">
            <TicketMetadataRow ccEmail={preview?.cc_email || 'guillaume@braux.ch'} priorityLabel={preview?.priority_label || 'Élevé'} />
            <ArcSessionPanel />

            <div className="space-y-1">
              <label htmlFor="ticket-title" className="text-xs uppercase tracking-wider text-zinc-500">Titre</label>
              <Input
                id="ticket-title"
                data-testid="ticket-title-input"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="ticket-description" className="text-xs uppercase tracking-wider text-zinc-500">Description</label>
              <Textarea
                id="ticket-description"
                data-testid="ticket-description-input"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={submitting}
                rows={14}
                className="font-mono text-xs"
              />
            </div>

            {error ? <SubmissionErrorPanel message={error.message} screenshotBase64={error.screenshot} needsArcSession={error.needsArcSession} /> : null}
          </div>
        )}

        {!loadingPreview && (
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="ticket-cancel-btn"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              {error ? 'Fermer' : 'Annuler'}
            </Button>
            <Button
              type="button"
              data-testid="ticket-submit-btn"
              disabled={submitting}
              onClick={handleSubmit}
              className="gap-2"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Envoi au portail Arc...</>
              ) : (
                <><Send className="h-4 w-4" /> {error ? 'Réessayer' : 'Envoyer au support Arc'}</>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CreateTicketButton({ messageId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="create-ticket-btn"
        className="gap-2"
        onClick={() => setOpen(true)}
      >
        <LifeBuoy className="h-4 w-4" />
        Créer un ticket de support
      </Button>
      <CreateTicketModal open={open} onOpenChange={setOpen} messageId={messageId} />
    </>
  );
}
